/*
 * Range can upsell step, shown between "Add" and checkout.
 *
 *   RangeCanUpsell.prompt({
 *     title: 'PMC .223 Range Can',
 *     tier: 'range' | 'stock',
 *     range: { id, price, label, cents },          // price = formatted money, cents = same price in cents
 *     stock: { id, price, label, extra, cents },   // extra = "+$310 for 380 more rounds"
 *     suggestions: [{ title, image, variantId, price, label, cents }],
 *     pairDiscount: true | false,           // 5% automatic discount is live
 *     engraving: 'MILLER'                   // optional; when empty the sheet offers a free name field
 *   }) -> Promise<{ variantId, extraItems: [{ id, quantity }], engraving?, cancelled? }>
 *
 * At most ONE sheet per add:
 * Range Day (default) -> one sheet: upgrade to Stock-Up, or keep Range Day.
 * Range Day picked on purpose (skipUpgrade) -> the second-can sheet instead, when pairDiscount is live.
 * Stock-Up picked  -> offer a second can (only when pairDiscount is live).
 * Closing the sheet (X, Escape or a tap outside) continues to checkout with the shopper's
 * original pick, same as "No thanks" (owner decision). Any name typed in the sheet is kept.
 * Anything else, or no data, resolves immediately with the original pick.
 */
(function () {
  if (window.RangeCanUpsell) return;

  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const CLOSE = '<button type="button" class="rcu__close" aria-label="Close" data-rcu-close><span aria-hidden="true">&times;</span></button>';
  const FOCUSABLE = 'button:not([disabled]):not([hidden]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

  // Dollar savings only (never a per-round price): whole dollars drop the cents.
  const money = (cents) => {
    const value = Math.round(Number(cents) || 0);
    const whole = value % 100 === 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(value / 100);
    } catch (e) {
      return `$${(value / 100).toFixed(whole ? 0 : 2)}`;
    }
  };

  /* Open on the dialog itself, scrolled to the top: the headline is read first and
     no button that raises the order total ever holds the initial focus. */
  function focusPanel(root) {
    const panel = root.querySelector('.rcu__panel');
    if (!panel) return;
    panel.scrollTop = 0;
    try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
  }

  function build(html) {
    const root = document.createElement('div');
    root.className = 'rcu';
    root.innerHTML = `<div class="rcu__scrim" data-rcu-dismiss></div>
      <div class="rcu__panel" role="dialog" aria-modal="true" aria-labelledby="rcu-title" tabindex="-1">${CLOSE}${html}</div>`;
    root._opener = document.activeElement;
    // Keep keyboard and screen-reader users inside the sheet.
    root._inerted = Array.from(document.body.children).filter((el) => !el.inert && el.tagName !== 'SCRIPT');
    root._inerted.forEach((el) => { el.inert = true; });
    document.body.appendChild(root);
    document.documentElement.classList.add('rcu-open');
    const panel = root.querySelector('.rcu__panel');
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    requestAnimationFrame(() => root.classList.add('is-open'));
    return root;
  }

  function close(root) {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('rcu-open');
    (root._inerted || []).forEach((el) => { el.inert = false; });
    const opener = root._opener;
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
      try { opener.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }
    setTimeout(() => root.remove(), 200);
  }

  /* X button, Escape and scrim tap = "No thanks": continue with the original pick. */
  function bindCancel(root, cancel) {
    root.querySelector('[data-rcu-dismiss]').addEventListener('click', cancel);
    root.querySelector('[data-rcu-close]').addEventListener('click', cancel);
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape') cancel(); });
  }

  function engravingLine(opts) {
    const name = String(opts.engraving || '').trim();
    if (!name) {
      // Collapsed by default: the sheet stays a one-tap decision; the field opens only on request.
      return `<div class="rcu__engrave-ask">
          <button type="button" class="rcu__engrave-toggle" aria-expanded="false" data-rcu-engrave-toggle>+ Add a name, engraved free <small>(optional)</small></button>
          <label class="rcu__engrave rcu__engrave--field" hidden data-rcu-engrave-field>
            <span>Name to engrave, free</span>
            <input type="text" maxlength="20" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="e.g. J. MILLER" aria-describedby="rcu-engrave-hint" data-rcu-engrave>
            <small class="rcu__engrave-hint" id="rcu-engrave-hint">Up to 20 characters. Letters, numbers, . ' &amp; - <span data-rcu-engrave-count aria-live="polite">0/20</span></small>
          </label>
        </div>`;
    }
    // Already typed in step 2: confirm it carried over, with a way to change it here.
    return `<div class="rcu__engrave-ask">
          <p class="rcu__engrave rcu__engrave--set" data-rcu-engrave-set>Engraving, free: <strong>${esc(name)}</strong> <button type="button" class="rcu__engrave-edit" aria-expanded="false" data-rcu-engrave-toggle>Edit</button></p>
          <label class="rcu__engrave rcu__engrave--field" hidden data-rcu-engrave-field>
            <span>Name to engrave, free</span>
            <input type="text" maxlength="20" autocomplete="off" autocapitalize="characters" spellcheck="false" value="${esc(name)}" aria-describedby="rcu-engrave-hint" data-rcu-engrave>
            <small class="rcu__engrave-hint" id="rcu-engrave-hint">Up to 20 characters. Letters, numbers, . ' &amp; - <span data-rcu-engrave-count aria-live="polite">${name.length}/20</span></small>
          </label>
        </div>`;
  }

  function bindEngraving(root) {
    const input = root.querySelector('[data-rcu-engrave]');
    if (!input) return () => '';
    const count = root.querySelector('[data-rcu-engrave-count]');
    const toggle = root.querySelector('[data-rcu-engrave-toggle]');
    const field = root.querySelector('[data-rcu-engrave-field]');
    const setLine = root.querySelector('[data-rcu-engrave-set]');
    if (toggle && field) {
      toggle.addEventListener('click', () => {
        field.hidden = false;
        toggle.hidden = true;
        if (setLine) setLine.hidden = true;
        toggle.setAttribute('aria-expanded', 'true');
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
        input.scrollIntoView({ block: 'nearest' });
      });
    }
    input.addEventListener('focus', () => input.scrollIntoView({ block: 'nearest' }));
    // Same filter as the step 2 field, so what is typed here is what gets engraved.
    const clean = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9 .'&-]/g, '').slice(0, 20);
    input.addEventListener('input', () => {
      const pos = input.selectionStart;
      const before = input.value.length;
      input.value = clean(input.value);
      const shift = before - input.value.length;
      try { input.setSelectionRange(Math.max(0, pos - shift), Math.max(0, pos - shift)); } catch (e) { /* ignore */ }
      if (count) count.textContent = `${input.value.length}/20`;
    });
    return () => clean(input.value).trim();
  }

  function productName(opts) {
    return String(opts.title || '').replace(/\s*Range Can\s*$/i, '');
  }

  function canPair(opts) {
    return Boolean(opts.pairDiscount && opts.range && opts.range.id);
  }

  // Same anchor as the tier cards: strike price above, squared SAVE tag below (only when there is one).
  function priceBlock(tier) {
    const compare = tier.compare ? `<s>${esc(tier.compare)}</s>` : '';
    const save = tier.save ? `<em class="rcu__save">Save ${esc(tier.save)}</em>` : '';
    return `${compare}<b>${esc(tier.price)}</b>${save}`;
  }

  function upgrade(opts) {
    const r = opts.range;
    const s = opts.stock;
    return new Promise((resolve) => {
      const root = build(`
        <p class="rcu__eyebrow">Only in the Stock-Up</p>
        <h2 id="rcu-title" class="rcu__title">Add the Land of the Free can?</h2>
        <p class="rcu__text">${esc(s.extra || 'The Land of the Free can only comes in the Stock-Up, with more rounds and more targets.')}</p>
        <div class="rcu__compare">
          <div class="rcu__col">
            <span>Your pick</span>
            <strong>${esc(r.label)}</strong>
            ${priceBlock(r)}
          </div>
          <div class="rcu__col rcu__col--best">
            <span>Both cans</span>
            <strong>${esc(s.label)}</strong>
            ${priceBlock(s)}
          </div>
        </div>
        ${engravingLine(opts)}
        <div class="rcu__actions">
          <button type="button" class="rcu__primary" data-rcu-yes>Yes, upgrade to Stock-Up &middot; ${esc(s.price)}</button>
          <button type="button" class="rcu__secondary" data-rcu-no>No thanks, keep Range Day</button>
        </div>`);
      let done = false;
      const engraving = bindEngraving(root);
      const finish = (value) => { if (done) return; done = true; close(root); resolve(value); };
      const go = (variantId) => {
        const name = engraving();
        finish(name ? { variantId, extraItems: [], engraving: name } : { variantId, extraItems: [] });
      };
      root.querySelector('[data-rcu-yes]').addEventListener('click', () => go(s.id));
      root.querySelector('[data-rcu-no]').addEventListener('click', () => go(r.id));
      bindCancel(root, () => go(r.id));
      focusPanel(root);
    });
  }

  /* 2+ range cans in the cart take 5% off automatically. baseId is the can already picked. */
  function pair(opts, baseId) {
    const r = opts.range || {};
    const s = opts.stock || {};
    const name = productName(opts);
    const baseIsStock = Boolean(s.id) && String(baseId) === String(s.id);
    const same = baseIsStock ? s : r;
    const list = (opts.suggestions || [])
      .filter((item) => String(item.variantId) !== String(baseId) && String(item.variantId) !== String(r.id));
    const options = [];
    if (same.id) {
      options.push({
        title: `Double it: a second ${name} ${baseIsStock ? 'Stock-Up' : 'Range Day'} Can`.replace(/\s+/g, ' '),
        label: same.label || 'Same can, second one',
        price: same.price,
        cents: same.cents,
        image: '',
        variantId: same.id,
        double: true
      });
    }
    if (baseIsStock && r.id) {
      options.push({
        title: `Add a ${name} Range Day Can`.replace(/\s+/g, ' '),
        label: r.label || '',
        price: r.price,
        cents: r.cents,
        image: '',
        variantId: r.id,
        mark: '+1'
      });
    }
    // Three choices at most: same-caliber rows first, other calibers fill what is left.
    options.push(...list.slice(0, Math.max(0, 3 - options.length)));
    const baseCents = Number((baseIsStock ? s : r).cents) || 0;
    if (!options.length) return Promise.resolve({ variantId: baseId, extraItems: [] });

    return new Promise((resolve) => {
      const root = build(`
        <p class="rcu__eyebrow">2+ range cans = 5% off</p>
        <h2 id="rcu-title" class="rcu__title">Add a second can?</h2>
        <p class="rcu__text">Put 2 or more range cans in your cart and 5% comes off all of them, automatically at checkout. Doubling the same can counts.</p>
        <ul class="rcu__list" role="list">
          ${options.map((item, i) => `
            <li>
              <label class="rcu__item${item.double ? ' rcu__item--double' : ''}">
                <input type="checkbox" value="${esc(item.variantId)}" data-rcu-pick="${i}">
                ${item.image ? `<img src="${esc(item.image)}" alt="" width="72" height="72" loading="lazy">` : `<span class="rcu__item-mark" aria-hidden="true">${item.mark ? esc(item.mark) : '2&times;'}</span>`}
                <span class="rcu__item-copy"><strong>${esc(item.title)}</strong><small>${esc(item.label)}</small></span>
                <b>${esc(item.price)}</b>
              </label>
            </li>`).join('')}
        </ul>
        ${engravingLine(opts)}
        <div class="rcu__actions">
          <button type="button" class="rcu__primary" data-rcu-yes disabled>Add &amp; check out</button>
          <button type="button" class="rcu__secondary" data-rcu-no>No thanks, check out</button>
        </div>`);
      const yes = root.querySelector('[data-rcu-yes]');
      const picks = () => Array.from(root.querySelectorAll('[data-rcu-pick]:checked')).map((el) => el.value);
      root.querySelectorAll('[data-rcu-pick]').forEach((box) => box.addEventListener('change', () => {
        const chosen = Array.from(root.querySelectorAll('[data-rcu-pick]:checked'));
        const n = chosen.length;
        // 5% comes off every range can in the cart, the one already picked included.
        const extraCents = chosen.reduce((sum, el) => sum + (Number((options[Number(el.dataset.rcuPick)] || {}).cents) || 0), 0);
        const saving = Math.round((baseCents + extraCents) * 0.05);
        yes.disabled = n === 0;
        yes.textContent = n
          ? (baseCents && extraCents ? `Add ${n} & check out · save ${money(saving)}` : `Add ${n} & check out`)
          : 'Add & check out';
      }));
      let done = false;
      const engraving = bindEngraving(root);
      const finish = (value) => { if (done) return; done = true; close(root); resolve(value); };
      const take = (extra) => {
        const value = { variantId: baseId, extraItems: extra.map((id) => ({ id: Number(id), quantity: 1 })) };
        const nameText = engraving();
        if (nameText) value.engraving = nameText;
        finish(value);
      };
      yes.addEventListener('click', () => take(picks()));
      root.querySelector('[data-rcu-no]').addEventListener('click', () => take([]));
      bindCancel(root, () => take([]));
      focusPanel(root);
    });
  }

  /* ---------------------------------------------------------------- after-add confirmation
     Shared by the collection quick add and the product pages.

     offers(product, products, pickedVariant, isStock) -> up to 3 rows:
       1. the same can again, same tier ("Double up")
       2-3. companions picked per caliber, each with a reason tag: rifle buyers get the 9mm
            pistol can, 9mm and other handgun buyers get the 22LR for rimfire practice,
            shotgun buyers get the other 12 gauge load. Companions are Range Day cans, so
            every row is exactly one more can toward the 5%. Sold-out picks fall back to
            the same caliber family.
     product/products use the collection payload shape: { handle, title, tags, available,
     variants: [{ id, title, price, cents, count, unit, available }] }. */
  const HANDGUN = ['caliber-9mm', 'caliber-40-sw', 'caliber-380', 'caliber-45-acp', 'caliber-10mm', 'caliber-38-special', 'caliber-44-magnum'];
  const RIFLE = ['caliber-556', 'caliber-223', 'caliber-6mm-creedmoor', 'caliber-762x54r', 'caliber-22lr'];
  const SHOTGUN = ['caliber-12-gauge', 'caliber-410'];
  const shortTitle = (t) => String(t || '').replace(/ Loaded Range Can$/, '').replace(/ Range Can$/, '').replace(/ Can$/, '');
  const tagsOf = (p) => (p && p.tags || []).map((t) => String(t).toLowerCase());
  const familyOf = (p) => {
    const tags = tagsOf(p);
    return [HANDGUN, RIFLE, SHOTGUN].findIndex((fam) => fam.some((t) => tags.includes(t)));
  };

  function offers(product, products, pickedVariant, isStock) {
    const H = {
      nine: ['magtech-9mm-range-can', 'Pistol can'],
      rim: ['remington-22lr-range-can', 'Rimfire practice'],
      rifle: ['pmc-556-range-can', 'Rifle can'],
      r223: ['pmc-223-range-can', 'Also feeds a 5.56 rifle'],
      clays: ['federal-12-gauge-clays-range-can', 'Same shotgun: clays'],
      field: ['sterling-12-gauge-field-range-can', 'Same shotgun: field'],
    };
    const otherShotgun = product.handle === H.clays[0] ? H.field : H.clays;
    const PAIRS = {
      'caliber-556': [H.nine, H.r223],
      'caliber-223': [H.nine, H.rim],
      'caliber-9mm': [H.rim, H.rifle],
      'caliber-22lr': [H.nine, H.rifle],
      'caliber-6mm-creedmoor': [H.nine, H.rifle],
      'caliber-762x54r': [H.nine, H.rim],
      'caliber-12-gauge': [otherShotgun, H.nine],
      'caliber-410': [H.rim, H.nine],
    };
    const tags = tagsOf(product);
    const key = Object.keys(PAIRS).find((k) => tags.includes(k));
    const plan = key ? PAIRS[key] : [H.rim, [H.nine[0], '9mm practice']]; // every other handgun caliber
    const usable = (p) => p && p.handle !== product.handle && p.available && p.variants && p.variants[0] && p.variants[0].available;
    const rows = [];
    if (pickedVariant && pickedVariant.available !== false) {
      rows.push({ product, variant: pickedVariant, stock: isStock, title: shortTitle(product.title), tag: 'Double up' });
    }
    const used = new Set([product.handle]);
    plan.forEach(([handle, tag]) => {
      const p = (products || []).find((x) => x.handle === handle);
      if (rows.length < 3 && usable(p) && !used.has(p.handle)) {
        used.add(p.handle);
        rows.push({ product: p, variant: p.variants[0], stock: false, title: shortTitle(p.title), tag });
      }
    });
    const fam = familyOf(product);
    const rest = (products || []).filter((p) => usable(p) && !used.has(p.handle));
    [...rest.filter((p) => familyOf(p) === fam), ...rest.filter((p) => familyOf(p) !== fam)]
      .slice(0, Math.max(0, 3 - rows.length))
      .forEach((p) => rows.push({ product: p, variant: p.variants[0], stock: false, title: shortTitle(p.title), tag: 'Another caliber' }));
    // Range Day buyers: the Stock-Up upgrade leads (it replaces the can, it does not add one).
    const stockVariant = product.variants && product.variants[1];
    if (!isStock && stockVariant && stockVariant.available !== false) {
      // Upgrade replaces "Double up": never offer the bigger can and a second of the same can together.
      const rest = rows.filter((r) => r.tag !== 'Double up');
      return [{ kind: 'upgrade', product, variant: stockVariant, stock: true, title: shortTitle(product.title), tag: 'Upgrade' }, ...rest].slice(0, 3);
    }
    return rows.slice(0, 3);
  }

  /* One header for the offer list on both surfaces. */
  function offersHead(rows, cans) {
    const upgrade = rows[0] && rows[0].kind === 'upgrade';
    if ((cans || 1) >= 2) return '<strong>Add another can:</strong> 5% off every range can.';
    return upgrade ? '<strong>Upgrade, or add a second can:</strong> 5% off both.' : '<strong>Add a second can:</strong> 5% off both.';
  }

  /* The UpCart app patches window.fetch and slides its drawer open on every /cart/add.js.
     A same-origin blank iframe has its own unpatched fetch. Falls back to window.fetch. */
  let cleanFrame = null;
  function cartFetch(path, options) {
    let f = window.fetch.bind(window);
    try {
      if (!cleanFrame || !cleanFrame.isConnected) {
        cleanFrame = document.createElement('iframe');
        cleanFrame.hidden = true;
        cleanFrame.setAttribute('aria-hidden', 'true');
        cleanFrame.tabIndex = -1;
        document.body.appendChild(cleanFrame);
      }
      if (cleanFrame.contentWindow && cleanFrame.contentWindow.fetch) f = cleanFrame.contentWindow.fetch.bind(cleanFrame.contentWindow);
    } catch (e) { /* page fetch */ }
    return f(location.origin + path, options);
  }

  /* Shipping insurance, by the same rules as the rest of the site (subscription-insurance-core.js): the line that fits
     the cart once deltaCents of merchandise is added, to send in the same /cart/add.js request. A tier that no longer
     fits is removed here. Resolves null when nothing needs adding or the core isn't on the page. */
  async function insuranceFor(deltaCents) {
    const core = window.SubscriptionInsuranceCore;
    const settings = (window.theme && window.theme.shopSettings) || {};
    if (!core || !core.determineInsuranceAction || !settings.shippingInsuranceEnabled) return null;
    try {
      if (core.clearCartCache) core.clearCartCache();
      const action = await core.determineInsuranceAction(false, null, deltaCents ? { merchandiseSubtotalDelta: deltaCents } : undefined);
      return await core.executeInsuranceAction(action);
    } catch (e) {
      return null;
    }
  }

  /* After an add that carried insurance: leave exactly one insurance line at quantity 1 (an identical add merges into
     an existing line, and a discount code re-keys lines). */
  async function settleInsurance(item) {
    const core = window.SubscriptionInsuranceCore;
    if (!core) return;
    try {
      if (item && core.removeReplacedInsurance) await core.removeReplacedInsurance(item);
      if (core.clearCartCache) core.clearCartCache();
    } catch (e) { /* checkout re-checks the cart */ }
  }

  /* Range cans in the cart (items that carry a Package property or belong to the lineup). */
  async function rangeCanCount(products) {
    const handles = new Set((products || []).map((p) => p.handle));
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart');
    const cart = await res.json();
    return cart.items.reduce((n, item) => n + (handles.has(item.handle) || (item.properties && item.properties.Package) ? item.quantity : 0), 0);
  }

  const placement = (stock) => (stock ? 'Below the design on both cans' : 'Below We The People engraving');

  function offerWhat(r, picked) {
    const v = r.variant;
    const qty = v.count ? `${Number(v.count).toLocaleString('en-US')} ${v.unit || 'rds'}` : '';
    if (r.kind === 'upgrade') {
      // Spell out all three things the upgrade adds: more ammo, the second engraved can, more targets.
      const more = picked && picked.count && v.count > picked.count
        ? `+${Number(v.count - picked.count).toLocaleString('en-US')} ${v.unit || 'rds'}`
        : qty;
      return `${more} · + Land of the Free can · + more targets`;
    }
    return r.stock ? `Stock-Up · ${qty} · 2 cans` : `Range Day · ${qty}`;
  }

  /* Rows markup + wiring, used by both surfaces. onAdded runs after a successful add. */
  function renderOffers(list, rows, opts) {
    const picked = opts.pickedVariant;
    list.innerHTML = rows.map((r, i) => {
      const upgrade = r.kind === 'upgrade';
      const step = upgrade && picked && picked.cents && r.variant.cents ? Number(r.variant.cents) - Number(picked.cents) : 0;
      const price = upgrade && step > 0 ? `+${money(step)}` : esc(r.variant.price);
      const name = upgrade ? `${esc(r.title)} Stock-Up` : esc(r.title);
      // Pages that pass the two-can photo (the range cans collection) show it on the Upgrade, so the second can is seen.
      const thumb = upgrade && r.product && r.product.stockImage
        ? `<img class="rcu__offer-img" src="${esc(r.product.stockImage)}" alt="We The People and Land of the Free cans" width="76" height="51" loading="lazy">`
        : '';
      return `
        <div class="rcu__offer${upgrade ? ' rcu__offer--upgrade' : ''}${thumb ? ' rcu__offer--img' : ''}">
          ${thumb}
          <span class="rcu__offer-copy">
            <i class="rcu__offer-tag">${esc(r.tag)}</i>
            <strong>${name}</strong>
            <small>${esc(offerWhat(r, picked))}</small>
          </span>
          <span class="rcu__offer-buy">
            <b class="rcu__offer-price">${price}</b>
            <button type="button" class="rcu__offer-add" data-rcu-offer="${i}" aria-label="${upgrade ? 'Upgrade to' : 'Add'} ${name} and check out">${upgrade ? 'Upgrade' : 'Add'}</button>
          </span>
        </div>`;
    }).join('');
    list.querySelectorAll('[data-rcu-offer]').forEach((btn) => btn.addEventListener('click', async () => {
      const r = rows[Number(btn.dataset.rcuOffer)];
      if (!r || btn.disabled) return;
      list.querySelectorAll('[data-rcu-offer]').forEach((b) => { b.disabled = true; });
      btn.textContent = 'Adding...';
      const props = { Package: r.stock ? 'Stock-Up Can' : 'Range Day Can', Delivery: 'One-time purchase' };
      if (opts.engraving) {
        props['Custom Name Engraving'] = opts.engraving;
        props['Engraving Placement'] = placement(r.stock);
      }
      const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      try {
        if (r.kind === 'upgrade') {
          // Swap one Range Day can for the Stock-Up, keeping the shopper's name and other properties.
          const cart = await (await cartFetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
          const line = (cart.items || []).find((item) => String(item.variant_id) === String(picked && picked.id));
          if (line) {
            Object.assign(props, line.properties || {}, { Package: 'Stock-Up Can' });
            if (props['Custom Name Engraving']) props['Engraving Placement'] = placement(true);
            const removed = await cartFetch('/cart/change.js', { method: 'POST', headers: json, body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - 1) }) });
            if (!removed.ok) throw new Error('change');
          }
        }
        // A second can can move the cart into a higher insurance tier: resize it in the same request.
        const insurance = await insuranceFor(Number(r.variant.cents) || 0);
        const items = [{ id: Number(r.variant.id), quantity: 1, properties: props }];
        if (insurance) items.push(insurance);
        const res = await cartFetch('/cart/add.js', {
          method: 'POST',
          headers: json,
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error('add');
        if (insurance) await settleInsurance(insurance);
        btn.textContent = 'Going to checkout...';
        window.location.href = opts.checkoutUrl || '/checkout';
      } catch (e) {
        list.querySelectorAll('[data-rcu-offer]').forEach((b) => { b.disabled = false; });
        btn.textContent = 'Try again';
      }
    }));
  }

  /* Product page confirmation sheet. Resolves when closed (X / Escape / scrim); the offers
     and "Continue to checkout" navigate away. */
  function confirm(opts) {
    const rows = offers(opts.product, opts.products, opts.pickedVariant, opts.isStock);
    const cans = opts.cans || 1;
    return new Promise((resolve) => {
      const list = rows;
      const showOffers = opts.pairDiscount && list.length;
      const root = build(`
        <p class="rcu__eyebrow">Added to cart</p>
        <h2 id="rcu-title" class="rcu__title">${esc(shortTitle(opts.product.title))} ${opts.isStock ? 'Stock-Up' : 'Range Day'}</h2>
        ${opts.engraving ? `<p class="rcu__engraved"><strong>${esc(opts.engraving)}</strong> on ${opts.isStock ? 'both cans' : 'your can'}. Check the spelling.</p>` : ''}
        ${showOffers ? `
        <div class="rcu__offers">
          <p class="rcu__offers-head">${offersHead(list, cans)}</p>
          <div class="rcu__offer-list" data-rcu-offers></div>
        </div>` : ''}
        ${opts.pairDiscount && cans >= 2 && !showOffers ? `<p class="rcu__text"><strong>${cans} range cans in your cart.</strong> 5% comes off every one automatically at checkout.</p>` : ''}
        <a class="rcu__continue" href="${esc(opts.checkoutUrl || '/checkout')}" data-rcu-continue>Checkout <span aria-hidden="true">&rarr;</span></a>`);
      root.classList.add('rcu--confirm');
      if (showOffers) renderOffers(root.querySelector('[data-rcu-offers]'), list, Object.assign({}, opts, { cans }));
      const cont = root.querySelector('[data-rcu-continue]');
      cont.addEventListener('click', () => { cont.firstChild.textContent = 'Going to checkout... '; });
      let done = false;
      const finish = () => { if (done) return; done = true; close(root); resolve({ closed: true }); };
      root.querySelector('[data-rcu-dismiss]').addEventListener('click', finish);
      root.querySelector('[data-rcu-close]').addEventListener('click', finish);
      root.addEventListener('keydown', (e) => { if (e.key === 'Escape') finish(); });
      focusPanel(root);
    });
  }

  window.RangeCanUpsell = {
    offers,
    renderOffers,
    offersHead,
    cartFetch,
    insuranceFor,
    settleInsurance,
    rangeCanCount,
    confirm,
    prompt(opts) {
      if (!opts || !opts.range || !opts.stock) {
        return Promise.resolve({ variantId: opts && (opts.tier === 'stock' ? opts.stock?.id : opts.range?.id), extraItems: [] });
      }
      if (opts.tier === 'range' && opts.skipUpgrade) {
        // Range Day picked on purpose: one sheet only, the 2+ cans offer (or straight through).
        if (canPair(opts)) return pair(opts, opts.range.id);
        return Promise.resolve({ variantId: opts.range.id, extraItems: [] });
      }
      if (opts.tier === 'range' && opts.stock.id) return upgrade(opts);
      if (opts.tier === 'stock' && canPair(opts)) return pair(opts, opts.stock.id);
      return Promise.resolve({ variantId: opts.tier === 'stock' ? opts.stock.id : opts.range.id, extraItems: [] });
    }
  };
})();
